import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";

// ── Async thunks ──────────────────────────────────────────────────────────────

export const fetchFormations = createAsyncThunk(
  "formations/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get("/formations", { params });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur chargement");
    }
  }
);

export const fetchFormationsOuvertes = createAsyncThunk(
  "formations/fetchOuvertes",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get("/formations/ouvertes");
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur chargement");
    }
  }
);

export const fetchFormationsEnAttente = createAsyncThunk(
  "formations/fetchEnAttente",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get("/formations/en-attente");
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur chargement");
    }
  }
);

export const fetchFormationsByFormateur = createAsyncThunk(
  "formations/fetchByFormateur",
  async (formateurId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/formations/formateur/${formateurId}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur chargement");
    }
  }
);

export const createFormation = createAsyncThunk(
  "formations/create",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post("/formations", formData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur création");
    }
  }
);

export const updateFormation = createAsyncThunk(
  "formations/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.put(`/formations/${id}`, formData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur modification");
    }
  }
);

export const deleteFormation = createAsyncThunk(
  "formations/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/formations/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur suppression");
    }
  }
);

export const accepterFormation = createAsyncThunk(
  "formations/accepter",
  async ({ id, formateurId }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.patch(`/formations/${id}/accepter`, { formateurId });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur acceptation");
    }
  }
);

export const refuserFormation = createAsyncThunk(
  "formations/refuser",
  async ({ id, formateurId }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.patch(`/formations/${id}/refuser`, { formateurId });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur refus");
    }
  }
);

export const fetchFormationsRefusees = createAsyncThunk(
  "formations/fetchFormationsRefusees",
  async (formateurId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/formations/formateur/${formateurId}/refusees`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur chargement");
    }
  }
);

export const annulerDemande = createAsyncThunk(
  "formations/annulerDemande",
  async ({ id, formateurId }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.patch(`/formations/${id}/annuler-demande`, { formateurId });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur annulation");
    }
  }
);

export const affecterFormation = createAsyncThunk(
  "formations/affecter",
  async ({ id, formateurId }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.patch(`/formations/${id}/affecter`, { formateurId });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur affectation");
    }
  }
);

export const demanderFormation = createAsyncThunk(
  "formations/demander",
  async ({ id, formateurId }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.patch(`/formations/${id}/demander`, { formateurId });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur demande");
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const formationSlice = createSlice({
  name: "formations",
  initialState: {
    list:      [],
    ouvertes:  [],
    enAttente: [],
    refusees:  [],
    loading:   false,
    error:     null,
    success:   null,
    total:     0,
    page:      1,
    pages:     1,
  },
  reducers: {
    clearFormationMessages: (state) => {
      state.error   = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // fetchFormations
      .addCase(fetchFormations.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchFormations.fulfilled, (state, action) => {
        state.loading = false;
        state.list = Array.isArray(action.payload) ? action.payload : (action.payload.data || []);
        state.total = action.payload.total ?? state.list.length;
        state.page = action.payload.page ?? 1;
        state.pages = action.payload.pages ?? 1;
      })
      .addCase(fetchFormations.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      // fetchFormationsOuvertes
      .addCase(fetchFormationsRefusees.fulfilled, (state, action) => {
        state.refusees = action.payload;
      })
      .addCase(fetchFormationsOuvertes.fulfilled, (state, action) => {
        state.ouvertes = Array.isArray(action.payload) ? action.payload : (action.payload.data || []);
      })

      // fetchFormationsEnAttente
      .addCase(fetchFormationsEnAttente.fulfilled, (state, action) => {
        state.enAttente = Array.isArray(action.payload) ? action.payload : (action.payload.data || []);
      })

      // fetchFormationsByFormateur
      .addCase(fetchFormationsByFormateur.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload) ? action.payload : (action.payload.data || []);
      })

      // createFormation
      .addCase(createFormation.fulfilled, (state, action) => {
        state.list.push(action.payload);
        state.success = "Formation créée avec succès.";
      })
      .addCase(createFormation.rejected, (state, action) => { state.error = action.payload; })

      // updateFormation
      .addCase(updateFormation.fulfilled, (state, action) => {
        const idx = state.list.findIndex((f) => f._id === action.payload._id);
        if (idx !== -1) state.list[idx] = action.payload;
        state.success = "Formation mise à jour.";
      })
      .addCase(updateFormation.rejected, (state, action) => { state.error = action.payload; })

      // deleteFormation
      .addCase(deleteFormation.fulfilled, (state, action) => {
        state.list    = state.list.filter((f) => f._id !== action.payload);
        state.enAttente = state.enAttente.filter((f) => f._id !== action.payload);
        state.success = "Formation supprimée.";
      })
      .addCase(deleteFormation.rejected, (state, action) => { state.error = action.payload; })

      // accepterFormation — formation fully assigned, remove from enAttente
      .addCase(accepterFormation.fulfilled, (state, action) => {
        const idx = state.list.findIndex((f) => f._id === action.payload._id);
        if (idx !== -1) state.list[idx] = action.payload;
        state.enAttente = state.enAttente.filter((f) => f._id !== action.payload._id);
        state.success = "Formation acceptée.";
      })
      .addCase(accepterFormation.rejected, (state, action) => { state.error = action.payload; })

      // affecterFormation — direct assign
      .addCase(affecterFormation.fulfilled, (state, action) => {
        const idx = state.list.findIndex((f) => f._id === action.payload._id);
        if (idx !== -1) state.list[idx] = action.payload;
        const idx2 = state.enAttente.findIndex((f) => f._id === action.payload._id);
        if (idx2 !== -1) state.enAttente[idx2] = action.payload;
        state.success = "Formateur affecté avec succès.";
      })
      .addCase(affecterFormation.rejected, (state, action) => { state.error = action.payload; })

      // refuserFormation — update in place, only remove from enAttente if no demandes left
      .addCase(refuserFormation.fulfilled, (state, action) => {
        const idx = state.list.findIndex((f) => f._id === action.payload._id);
        if (idx !== -1) state.list[idx] = action.payload;
        if (action.payload.demandes?.length === 0) {
          state.enAttente = state.enAttente.filter((f) => f._id !== action.payload._id);
        } else {
          const idx2 = state.enAttente.findIndex((f) => f._id === action.payload._id);
          if (idx2 !== -1) state.enAttente[idx2] = action.payload;
        }
        state.success = "Demande refusée.";
      })
      .addCase(refuserFormation.rejected, (state, action) => { state.error = action.payload; })

      // demanderFormation
      .addCase(demanderFormation.fulfilled, (state) => {
        state.success = "Demande envoyée avec succès.";
      })
      .addCase(demanderFormation.rejected, (state, action) => { state.error = action.payload; });
  },
});

export const { clearFormationMessages } = formationSlice.actions;
export default formationSlice.reducer;
